// components/PeriodForm/PeriodFormUI.jsx
import { F, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import React from 'react';
import { Button, Popconfirm } from 'antd';
import VirtualizedList from 'rc-virtual-list';
import uniqBy from 'lodash/uniqBy';
import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import SameDayTimeRangePicker from '@/components/MyDateTimePicker/SameDayRagePicker';
import MyDateTimeRangePicker from '@/components/MyDateTimePicker';
import DateFormat from '@/components/format/DateFormat';
import { combinePickedDateAndTime, toPickedWallClock } from '@/utils/date';
import { useIntl } from 'umi';
import styles from './index.less';

const ITEM_HEIGHT = 50;

// rc-virtual-list calls itemKey with the item only (no index), and extra days share
// bare "HH:mm" start/end values, so the date has to be part of the key to keep it unique.
const getPeriodKey = (period) => `${period.date ?? ''}-${period.start}-${period.end}`;

export const PeriodFormUI = ({
                               mode,
                               periods: rawPeriods,
                               onAdd,
                               onRemove,
                               onChange,
                             }) => {
  const intl = useIntl();
  const [range, setRange] = React.useState([]);
  const [sameDayRange, setSameDayRange] = React.useState({});
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  // Exact duplicates would share a key and break the virtual list, and they add nothing
  const periods = React.useMemo(() => uniqBy(rawPeriods || [], getPeriodKey), [rawPeriods]);
  const entity = mode === 'block' ? INTL_ENTITY.BLOCK_PERIOD : INTL_ENTITY.ADDITIONAL_OPEN_HOURS;

  const getPeriodDate = (time: string, date?: string) => {
    if (!time) return date;
    if (typeof time === 'string' && time.length <= 8 && time.includes(':')) {
      const datePart = date ? date.split('T')[0] : '';
      return datePart ? `${datePart}T${time}` : time;
    }
    return time;
  };

  const transformSameDayRange = () => {
    const { date, start, end } = sameDayRange || {};
    return [
      combinePickedDateAndTime(date, start).format(),
      combinePickedDateAndTime(date, end).format(),
    ];
  };

  const handleAdd = () => {
    const rangeISODates = mode === 'block'
      ? range.map((date) => toPickedWallClock(date).format())
      : transformSameDayRange();

    const period = {
      start: rangeISODates[0],
      end: rangeISODates[1],
    };

    const isDuplicate = periods?.some(p => p.start === period.start && p.end === period.end);
    if (!isDuplicate) {
      onAdd?.(period);
    }

    if (mode === 'block') setRange([]);
    else setSameDayRange({});
  };

  const handleRemove = (index: number) => {
    const newPeriods = [...periods];
    newPeriods.splice(index, 1);
    onChange?.(newPeriods);
    onRemove?.(newPeriods);
  };

  const handleRemoveAll = () => {
    onChange?.([]);
    onRemove?.([]);
  };

  const isDisabled = () => {
    if (mode === 'block') return !(range?.[0] && range?.[1]);
    const { date, start, end } = sameDayRange || {};
    return !(date && start && end);
  };


  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      {/* Add Section */}
      <div className={styles.addLocationAvailability}>
        <div className={styles.inner}>
          {mode === 'open' ? (
            <SameDayTimeRangePicker
              notBeforeToday
              includeToday
              value={sameDayRange}
              onChange={setSameDayRange}
            />
          ) : (
            <MyDateTimeRangePicker
              styles={{ flex: 1, width: '100%', paddingRight: 15 }}
              onChange={setRange}
              ranges={range}
            />
          )}
          <Button
            className="button-secondary"
            disabled={isDisabled()}
            onClick={handleAdd}
            icon={<PlusOutlined/>}
            type="primary"
          >
          </Button>
        </div>
      </div>

      {/* List Section */}
      {periods?.length > 0 && (
        <div className={styles.blockPeriods}>
          {periods.length > 1 && (
            <div className={styles.listActions}>
              <Popconfirm
                onConfirm={handleRemoveAll}
                placement="topRight"
                getPopupContainer={(trigger) =>
                  trigger.closest('.ant-drawer-content') ||
                  rootRef.current ||
                  document.body
                }
                title={
                  <F
                    entity={entity}
                    action={INTL_ACTION.REMOVE_CONFIRMATION}
                    values={{entity: intl.formatMessage({id: `entity.${entity}.plural`})}}
                  />
                }
              >
                <Button type="link" size="small" danger>
                  <F id="common.actions.clear_all" defaultMessage="Clear all"/>
                </Button>
              </Popconfirm>
            </div>
          )}
          <VirtualizedList
            data={periods}
            height={Math.min(periods.length * ITEM_HEIGHT, 350)}
            itemHeight={ITEM_HEIGHT}
            className={styles.list}
            itemKey={getPeriodKey}
          >
            {(period, index) => (
              <div
                className={styles.periodRow}
                key={getPeriodKey(period)}
              >
                <div className={styles.inner}>
                  <div className={styles.periodInfo}>
                    {mode === 'block' ? (
                      <div className={styles.infoLine}>
                        <span className={styles.label}>
                          <F id="pages.locations.from" />
                        </span>
                        <DateFormat
                          ignoreLocalTimezone
                          format="YYYY-MM-DD HH:mm"
                          date={getPeriodDate(period.start, period.date)}
                        />
                        <span className={styles.separator}>→</span>
                        <span className={styles.label}>
                          <F id="pages.locations.to" />
                        </span>
                        <DateFormat
                          ignoreLocalTimezone
                          format="YYYY-MM-DD HH:mm"
                          date={getPeriodDate(period.end, period.date)}
                        />
                      </div>
                    ) : (
                      <div className={styles.infoLine}>
                        <DateFormat
                          ignoreLocalTimezone
                          format="YYYY-MM-DD"
                          date={getPeriodDate(period.start, period.date)}
                        />
                        <span className={styles.timeRange}>
                          <DateFormat
                            ignoreLocalTimezone
                            format="HH:mm"
                            date={getPeriodDate(period.start, period.date)}
                          />
                          {' → '}
                          <DateFormat
                            ignoreLocalTimezone
                            format="HH:mm"
                            date={getPeriodDate(period.end, period.date)}
                          />
                        </span>
                      </div>
                    )}
                  </div>
                  <div className={styles.action}>
                    <Popconfirm
                      // open={openIndex === index}
                      // onOpenChange={(open) => setOpenIndex(open ? index : null)}
                      onConfirm={() => {
                        setOpenIndex(null);
                        handleRemove(index);
                      }}
                      onCancel={() => setOpenIndex(null)}
                      placement="topRight"
                      destroyTooltipOnHide={true}
                      getPopupContainer={(trigger) =>
                        trigger.closest('.ant-drawer-content') ||
                        rootRef.current ||
                        document.body
                      }
                      title={
                        <F
                          entity={entity}
                          action={INTL_ACTION.REMOVE_CONFIRMATION}
                          defaultMessage="Are you sure to remove this {entity}?"
                        />
                      }
                    >
                      <Button
                        type="text"
                        size="small"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 0,
                          height: 24,
                          width: 24,
                        }}
                        icon={
                          <CloseCircleOutlined
                            style={{ color: '#787878', fontSize: 18 }}
                          />
                        }
                      />
                    </Popconfirm>
                  </div>
                </div>
              </div>
            )}
          </VirtualizedList>
        </div>
      )}
    </div>
  );
};
